import type { DaemonDisk } from "../api/types.js";

// 2.5.E.2: levyjen-lista yhdelle koneelle. Erottelee USB:n + sisäisen.

export function DisksList({ disks }: { disks: DaemonDisk[] }) {
  if (disks.length === 0) {
    return (
      <p className="text-sm italic text-slate-500">
        Ei aktiivisia levyjä viime 10 minuutin sisällä.
      </p>
    );
  }

  // Järjestä: sisäiset ekana, sitten USB, sitten muut.
  const sorted = [...disks].sort((a, b) => {
    const ap = a.isInternal ? 0 : a.isUsb ? 1 : 2;
    const bp = b.isInternal ? 0 : b.isUsb ? 1 : 2;
    return ap - bp || a.mountPath.localeCompare(b.mountPath);
  });

  return (
    <ul className="divide-y divide-slate-700">
      {sorted.map((d) => {
        const usedPct =
          d.sizeBytes && d.availableBytes
            ? Math.round((1 - d.availableBytes / d.sizeBytes) * 100)
            : null;
        return (
          <li key={d.deviceId} className="py-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">
                    {d.label ?? d.mountPath}
                  </span>
                  <DiskTypeBadge isInternal={d.isInternal} isUsb={d.isUsb} />
                </div>
                <code className="block truncate text-xs text-slate-400">
                  {d.mountPath} ({d.deviceId})
                </code>
              </div>
              <div className="text-right text-xs text-slate-400">
                {d.sizeBytes ? formatBytes(d.sizeBytes) : "—"}
              </div>
            </div>
            {usedPct !== null && (
              <div className="mt-2">
                <div className="h-1.5 w-full overflow-hidden rounded bg-slate-800">
                  <div
                    className={`h-full ${
                      usedPct > 90 ? "bg-red-500" : usedPct > 75 ? "bg-yellow-500" : "bg-cyan-500"
                    }`}
                    style={{ width: `${usedPct}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {usedPct}% käytetty
                  {d.availableBytes != null && ` — ${formatBytes(d.availableBytes)} vapaata`}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function DiskTypeBadge({ isInternal, isUsb }: { isInternal: boolean; isUsb: boolean }) {
  if (isInternal) {
    return (
      <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-300">
        Sisäinen
      </span>
    );
  }
  if (isUsb) {
    return (
      <span className="rounded bg-cyan-900/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-cyan-300">
        USB
      </span>
    );
  }
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}
